<script lang="ts">
export default {
  name: 'PromptMappingPage',
};
</script>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import AppButton from '@/components/v2/AppButton.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import ConfirmModal from '@/components/v2/ConfirmModal.vue';
import Badge from '@/components/ui/Badge.vue';
import Card from '@/components/ui/card/Card.vue';
import AddPromptMappingModal from '@/components/ui/AddPromptMappingModal.vue';
import BulkRemapPromptMappingModal from '@/components/ui/BulkRemapPromptMappingModal.vue';
import { aiService } from '@/services/AIService';
import { referenceService } from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import { useToast } from '@/composables/useToast';
import { filters } from '@/utilities/Filters';
import { PROMPT_VERSION_LATEST } from '@/types/ai';
import type { AIPrompt, AIPromptMapping } from '@/types/ai';
import type { LabelValue } from '@/types/label-value';
import type { DocumentType } from '@/types/v2/Document';
import { Pencil, Plus, Search as SearchIcon, Trash2, X as XIcon, Check } from '@lucide/vue';

interface CoverageType {
  id: number;
  name: string;
}

interface PromptMappingDisplay extends AIPromptMapping {
  promptName: string;
  coverageTypeName: string;
  documentTypeName: string;
}

const toast = useToast();

const promptMappings = ref<PromptMappingDisplay[]>([]);
const prompts = ref<AIPrompt[]>([]);
const coverageTypes = ref<CoverageType[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const loadingMappings = ref(false);
const filterText = ref('');
const selectedIds = ref<Set<number>>(new Set());
const searchInputRef = ref<HTMLInputElement | null>(null);

const addModalRef = ref<InstanceType<typeof AddPromptMappingModal> | null>(null);
const bulkModalRef = ref<InstanceType<typeof BulkRemapPromptMappingModal> | null>(null);
const confirmModalRef = ref<InstanceType<typeof ConfirmModal> | null>(null);

type EditingField = 'prompt' | 'version';
const editingRowId = ref<number | null>(null);
const editingField = ref<EditingField | null>(null);
const draftPromptId = ref('');
const draftVersion = ref('');
const draftUseLatest = ref(false);
const isSavingEdit = ref(false);

onMounted(async () => {
  try {
    await Promise.all([loadPrompts(), loadCoverageTypes(), loadDocumentTypes()]);
    await loadPromptMappings();
  } catch (error) {
    console.error('Failed to load initial data:', error);
  }
});

const loadPrompts = async () => {
  try {
    prompts.value = await aiService.getAllPrompts('internal');
  } catch (error) {
    console.error('Failed to load prompts:', error);
  }
};

const loadCoverageTypes = async () => {
  try {
    const response = await referenceService.coverageTypes();
    coverageTypes.value = response.map((item: LabelValue) => ({
      id: item.value,
      name: item.label,
    }));
  } catch (error) {
    console.error('Failed to load coverage types:', error);
  }
};

const loadDocumentTypes = async () => {
  try {
    const allTypes = await documentService.getDocumentTypes();
    documentTypes.value = allTypes.filter((dt) => dt.domainName === 'Policy');
  } catch (error) {
    console.error('Failed to load document types:', error);
  }
};

const loadPromptMappings = async () => {
  loadingMappings.value = true;
  try {
    const mappings = await aiService.getPromptMappings();
    promptMappings.value = mappings.map((mapping) => {
      const prompt = prompts.value.find((p) => Number(p.promptId) === mapping.promptId);
      const coverageType = coverageTypes.value.find((ct) => ct.id === mapping.coverageTypeId);
      const documentType = documentTypes.value.find((dt) => dt.id === mapping.documentTypeId);
      return {
        ...mapping,
        promptName: prompt?.name || 'Unknown Prompt',
        coverageTypeName: coverageType?.name || 'Unknown Coverage',
        documentTypeName: documentType?.name || 'Unknown Document',
      };
    });
    // Drop selections for any rows no longer present.
    const presentIds = new Set(promptMappings.value.map((m) => m.id));
    selectedIds.value = new Set([...selectedIds.value].filter((id) => presentIds.has(id)));
  } catch (error) {
    console.error('Failed to load prompt mappings:', error);
    promptMappings.value = [];
  } finally {
    loadingMappings.value = false;
  }
};

// Latest published version per prompt — used to render the "Latest · v{n}" badge subtext.
const latestVersionByPromptId = computed<Record<number, string>>(() => {
  const result: Record<number, string> = {};
  for (const prompt of prompts.value) {
    if (prompt.isDraft) continue;
    const id = Number(prompt.promptId);
    const versionNumber = parseInt(prompt.version, 10);
    if (Number.isNaN(versionNumber)) continue;
    const existing = result[id];
    if (!existing || versionNumber > parseInt(existing, 10)) {
      result[id] = prompt.version;
    }
  }
  return result;
});

const publishedPromptIds = computed(() => {
  const ids = new Set<string>();
  for (const prompt of prompts.value) {
    if (!prompt.isDraft) ids.add(String(prompt.promptId));
  }
  return ids;
});

interface SelectOption {
  label: string;
  value: string;
}

const promptNameOptions = computed<SelectOption[]>(() => {
  const seen = new Set<string>();
  return prompts.value
    .filter((prompt) => {
      const id = String(prompt.promptId);
      if (seen.has(id) || !publishedPromptIds.value.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((prompt) => ({ label: prompt.name, value: String(prompt.promptId) }));
});

const versionOptionsFor = (promptId: number | string): SelectOption[] => {
  const idStr = String(promptId);
  return prompts.value.filter((p) => String(p.promptId) === idStr && !p.isDraft).map((p) => ({ label: `v${p.version}`, value: p.version }));
};

const filteredMappings = computed<PromptMappingDisplay[]>(() => {
  const sorted = [...promptMappings.value].sort((a, b) => a.coverageTypeName.localeCompare(b.coverageTypeName));
  if (!filterText.value) return sorted;
  const q = filterText.value.toLowerCase();
  return sorted.filter(
    (m) => m.promptName.toLowerCase().includes(q) || m.coverageTypeName.toLowerCase().includes(q) || m.documentTypeName.toLowerCase().includes(q),
  );
});

const allFilteredSelected = computed(() => filteredMappings.value.length > 0 && filteredMappings.value.every((m) => selectedIds.value.has(m.id)));

const someFilteredSelected = computed(() => filteredMappings.value.some((m) => selectedIds.value.has(m.id)) && !allFilteredSelected.value);

const hasSelection = computed(() => selectedIds.value.size > 0);

const handleSearchKeyUp = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    filterText.value = '';
    e.stopPropagation();
  }
};

const toggleRow = (id: number) => {
  cancelEdit();
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
};

const toggleAllFiltered = () => {
  cancelEdit();
  if (allFilteredSelected.value) {
    const next = new Set(selectedIds.value);
    for (const m of filteredMappings.value) next.delete(m.id);
    selectedIds.value = next;
  } else {
    const next = new Set(selectedIds.value);
    for (const m of filteredMappings.value) next.add(m.id);
    selectedIds.value = next;
  }
};

const handleAddMapping = () => {
  addModalRef.value?.show();
};

const handleBulkRemap = () => {
  if (selectedIds.value.size === 0) return;
  bulkModalRef.value?.show([...selectedIds.value]);
};

const handleDeleteMapping = async (mapping: PromptMappingDisplay) => {
  const ok = await confirmModalRef.value?.showConfirm(
    `Delete the mapping for ${mapping.coverageTypeName} / ${mapping.documentTypeName} (${mapping.promptName})?`,
  );
  if (!ok) return;
  try {
    await aiService.deletePromptMapping(mapping.id.toString());
    toast.success('Mapping deleted');
    await loadPromptMappings();
  } catch (error) {
    console.error('Failed to delete mapping:', error);
    toast.error('Failed to delete mapping');
  }
};

const startEditPrompt = (mapping: PromptMappingDisplay) => {
  editingRowId.value = mapping.id;
  editingField.value = 'prompt';
  draftPromptId.value = String(mapping.promptId);
};

const startEditVersion = (mapping: PromptMappingDisplay) => {
  editingRowId.value = mapping.id;
  editingField.value = 'version';
  draftUseLatest.value = mapping.promptVersion === PROMPT_VERSION_LATEST;
  draftVersion.value = draftUseLatest.value ? '' : mapping.promptVersion;
};

const cancelEdit = () => {
  editingRowId.value = null;
  editingField.value = null;
  draftPromptId.value = '';
  draftVersion.value = '';
  draftUseLatest.value = false;
};

const isEditing = (id: number, field: EditingField) => editingRowId.value === id && editingField.value === field;

const versionExistsForPrompt = (promptId: number, version: string) =>
  prompts.value.some((p) => Number(p.promptId) === promptId && p.version === version && !p.isDraft);

const saveEdit = async (mapping: PromptMappingDisplay) => {
  if (!editingField.value || editingRowId.value !== mapping.id) return;
  isSavingEdit.value = true;
  try {
    if (editingField.value === 'prompt') {
      if (!draftPromptId.value) {
        cancelEdit();
        return;
      }
      const newPromptId = Number(draftPromptId.value);
      if (newPromptId === mapping.promptId) {
        cancelEdit();
        return;
      }
      // If the previously-stored version doesn't exist on the new prompt, fall back
      // to 'latest' rather than letting the backend reject the update.
      const keepVersion = mapping.promptVersion === PROMPT_VERSION_LATEST || versionExistsForPrompt(newPromptId, mapping.promptVersion);
      const newVersion = keepVersion ? mapping.promptVersion : PROMPT_VERSION_LATEST;
      await aiService.updatePromptMapping(mapping.id, {
        promptId: newPromptId,
        promptVersion: newVersion,
      });
    } else {
      const newVersion = draftUseLatest.value ? PROMPT_VERSION_LATEST : draftVersion.value;
      if (!newVersion) return;
      if (newVersion === mapping.promptVersion) {
        cancelEdit();
        return;
      }
      await aiService.updatePromptMapping(mapping.id, {
        promptId: mapping.promptId,
        promptVersion: newVersion,
      });
    }
    toast.success('Mapping updated');
    cancelEdit();
    await loadPromptMappings();
  } catch (error) {
    console.error('Failed to update mapping:', error);
    toast.error('Failed to update mapping');
  } finally {
    isSavingEdit.value = false;
  }
};

const handleMappingSaved = async () => {
  await loadPromptMappings();
};
</script>

<template>
  <div>
    <div class="inner py-0">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <h1 class="pl-2 text-2xl">Prompt Mapping</h1>
          <AppButton @click="handleAddMapping">
            <Plus :size="16" />
            Add Mapping
          </AppButton>
          <AppButton
            v-if="selectedIds.size > 0"
            primary
            @click="handleBulkRemap">
            <Pencil :size="16" />
            Remap Selected ({{ selectedIds.size }})
          </AppButton>
        </div>

        <label class="search-bar input input-sm flex min-w-96 items-center gap-2">
          <input
            ref="searchInputRef"
            type="text"
            placeholder="Search prompt, coverage, or document type"
            class="grow"
            v-model="filterText"
            autocomplete="off"
            @keyup="handleSearchKeyUp" />
          <XIcon
            v-if="filterText && filterText.length > 0"
            :size="20"
            class="cursor-pointer"
            @click="filterText = ''" />
          <SearchIcon :size="20" />
        </label>
      </div>
    </div>

    <div class="inner">
      <Card class="border-slate-200 bg-white/80 p-4 backdrop-blur-sm">
        <div
          v-if="loadingMappings"
          class="py-8 text-center text-slate-500">
          Loading mappings...
        </div>

        <div
          v-else-if="promptMappings.length === 0"
          class="py-8 text-center text-slate-500">
          No prompt mappings found. Click "Add Mapping" to create one.
        </div>

        <div
          v-else-if="filteredMappings.length === 0"
          class="py-8 text-center text-slate-500">
          No mappings match "{{ filterText }}"
        </div>

        <table
          v-else
          id="prompt-mapping-list"
          class="apptable dense">
          <thead>
            <tr>
              <td class="nosort w-8 text-center">
                <input
                  type="checkbox"
                  class="checkbox border border-solid border-gray-400 checkbox-sm checkbox-primary"
                  :checked="allFilteredSelected"
                  :indeterminate="someFilteredSelected"
                  aria-label="Select all"
                  @change="toggleAllFiltered" />
              </td>
              <td class="text-left">Coverage Type</td>
              <td class="text-left">Document Type</td>
              <td class="text-left">Prompt</td>
              <td class="text-left">Version</td>
              <td class="nosort w-12 text-center"></td>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="mapping in filteredMappings"
              :key="mapping.id"
              class="group hover:bg-gray-50">
              <td class="text-center">
                <input
                  type="checkbox"
                  class="checkbox border border-solid border-gray-400 checkbox-sm checkbox-primary"
                  :checked="selectedIds.has(mapping.id)"
                  :aria-label="`Select mapping ${mapping.id}`"
                  @change="toggleRow(mapping.id)" />
              </td>
              <td
                class="text-left"
                v-html="filters.highlight(mapping.coverageTypeName, filterText)"></td>
              <td
                class="text-left"
                v-html="filters.highlight(mapping.documentTypeName, filterText)"></td>

              <td class="text-left">
                <div class="relative flex h-9 items-center">
                  <button
                    class="-ml-1 h-7 rounded px-2 text-left disabled:cursor-default enabled:cursor-pointer enabled:hover:bg-blue-50"
                    :class="{ invisible: isEditing(mapping.id, 'prompt') }"
                    :disabled="hasSelection"
                    @click="startEditPrompt(mapping)"
                    :title="hasSelection ? 'Clear selection to edit individual rows' : `Edit prompt for mapping ${mapping.id}`"
                    v-html="filters.highlight(mapping.promptName, filterText)"></button>
                  <div
                    v-if="isEditing(mapping.id, 'prompt')"
                    class="top--1.5 absolute right-5 z-20 flex items-center gap-2 rounded-md border border-gray-200 bg-white p-1 shadow-md">
                    <AppSelect
                      v-model="draftPromptId"
                      :options="promptNameOptions"
                      placeholder="Select a prompt..."
                      class="w-64 !py-1"
                      triggerClass="text-sm" />
                    <button
                      class="btn btn-sm btn-primary"
                      :disabled="isSavingEdit || !draftPromptId"
                      @click="saveEdit(mapping)"
                      title="Save">
                      <Check :size="16" />
                    </button>
                    <button
                      class="btn btn-ghost btn-sm"
                      :disabled="isSavingEdit"
                      @click="cancelEdit"
                      title="Cancel">
                      <XIcon :size="16" />
                    </button>
                  </div>
                </div>
              </td>

              <td class="text-left">
                <div class="relative flex h-9 items-center">
                  <button
                    class="-ml-1 flex h-7 items-center gap-2 rounded px-2 disabled:cursor-default enabled:cursor-pointer enabled:hover:bg-blue-50"
                    :class="{ invisible: isEditing(mapping.id, 'version') }"
                    :disabled="hasSelection"
                    @click="startEditVersion(mapping)"
                    :title="hasSelection ? 'Clear selection to edit individual rows' : `Edit version for mapping ${mapping.id}`">
                    <template v-if="mapping.promptVersion === PROMPT_VERSION_LATEST">
                      <Badge
                        variant="secondary"
                        class="text-xs"
                        >Latest</Badge
                      >
                      <span
                        v-if="latestVersionByPromptId[mapping.promptId]"
                        class="text-xs text-slate-500">
                        · v{{ latestVersionByPromptId[mapping.promptId] }}
                      </span>
                    </template>
                    <template v-else>v{{ mapping.promptVersion }}</template>
                  </button>
                  <div
                    v-if="isEditing(mapping.id, 'version')"
                    class="top--1.5 absolute right-5 z-20 flex items-center gap-2 rounded-md border border-gray-200 bg-white p-1 shadow-md">
                    <Checkbox
                      v-model="draftUseLatest"
                      label="Latest"
                      class="!p-0 [&_.label]:!p-0" />
                    <AppSelect
                      v-model="draftVersion"
                      :options="versionOptionsFor(mapping.promptId)"
                      placeholder="Select version"
                      :disabled="draftUseLatest"
                      class="w-32 !py-1"
                      triggerClass="text-sm" />
                    <button
                      class="btn btn-sm btn-primary"
                      :disabled="isSavingEdit || (!draftUseLatest && !draftVersion)"
                      @click="saveEdit(mapping)"
                      title="Save">
                      <Check :size="16" />
                    </button>
                    <button
                      class="btn btn-ghost btn-sm"
                      :disabled="isSavingEdit"
                      @click="cancelEdit"
                      title="Cancel">
                      <XIcon :size="16" />
                    </button>
                  </div>
                </div>
              </td>

              <td class="text-center">
                <button
                  class="btn opacity-30 btn-ghost transition-opacity btn-sm btn-error group-hover:opacity-100"
                  :disabled="hasSelection"
                  @click="handleDeleteMapping(mapping)"
                  :title="hasSelection ? 'Clear selection to delete individual rows' : 'Delete mapping'">
                  <Trash2 :size="16" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
    </div>

    <AddPromptMappingModal
      ref="addModalRef"
      :prompts="prompts"
      :coverage-types="coverageTypes"
      :document-types="documentTypes"
      @saved="handleMappingSaved" />

    <BulkRemapPromptMappingModal
      ref="bulkModalRef"
      :prompts="prompts"
      @saved="handleMappingSaved" />

    <ConfirmModal ref="confirmModalRef" />
  </div>
</template>
